import React from "react";
import { Box, Paper, Typography, Chip, Divider } from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";

const TrackingRouteProgress = ({ currentStopNumber, routeList }) => {
  // Find the first next stop index
  const firstNextIndex = routeList.findIndex((s) => s.stop_state === "next");
  const nextStopNumber =
    firstNextIndex !== -1 ? routeList[firstNextIndex].stop_number : null;

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Route Progress
      </Typography>
      <Box sx={{ overflowX: "auto", pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            width: "100%",
            minWidth: { xs: "max-content", sm: 0 },
            pb: 1,
            minHeight: 96,
          }}
        >
          {routeList.map((stop, idx) => {
            const stopState = stop.stop_state;
            const isCompleted = stopState === "completed";
            const isNext = stopState === "next";
            const stopName = stop.stop_name || stop.location;
            const stopNum = stop.stop_number || stop.stopNumber;

            return (
              <React.Fragment key={stop.stop_number || stop.stopId}>
                {/* Stop Node */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {/* Stop Circle */}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s",
                      backgroundColor: isCompleted
                        ? "#4caf50"
                        : stop.stop_number === nextStopNumber
                        ? "#1976d2"
                        : "#bdbdbd",
                      boxShadow:
                        stop.stop_number === nextStopNumber
                          ? "0 0 0 4px rgba(25, 118, 210, 0.2)"
                          : "none",
                      transform:
                        stop.stop_number === nextStopNumber
                          ? "scale(1.1)"
                          : "scale(1)",
                      ...(isCompleted && {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }),
                    }}
                  >
                    {isCompleted ? (
                      <CheckCircleIcon sx={{ color: "white", fontSize: 16 }} />
                    ) : isNext ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          backgroundColor: "white",
                          borderRadius: "50%",
                          animation: "pulse 1.5s infinite",
                        }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        color="white"
                        fontWeight="bold"
                      >
                        {stopNum}
                      </Typography>
                    )}
                  </Box>

                  {/* Stop Name */}
                  <Box sx={{ mt: 0.75, textAlign: "center", width: 100 }}>
                    <Typography
                      sx={{ fontSize: "10px", lineHeight: 1.2 }}
                      fontWeight="medium"
                      color={
                        isCompleted
                          ? "success.main"
                          : stop.stop_number === nextStopNumber
                          ? "primary.main"
                          : "text.secondary"
                      }
                    >
                      {stopName}
                    </Typography>
                    {isNext && stop.stop_number === firstNextIndex + 1 && (
                      <Box>
                        <Chip
                          label="Next"
                          size="small"
                          color="primary"
                          variant="filled"
                          sx={{ mt: 0.5, height: 16, fontSize: "9px", "& .MuiChip-label": { px: 0.75 } }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Connecting Line */}
                {idx < routeList.length - 1 && (
                  <Box
                    sx={{
                      height: 2,
                      transition: "all 0.3s",
                      backgroundColor: isCompleted ? "#4caf50" : "#bdbdbd",
                      flex: { xs: "0 0 48px", sm: 1 },
                      minWidth: { xs: 48, sm: 24 },
                      mt: 2,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Legend */}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#4caf50",
            }}
          />
          <Typography sx={{ fontSize: "10px" }} color="text.secondary">
            Completed
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#1976d2",
              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
            }}
          />
          <Typography sx={{ fontSize: "10px" }} color="text.secondary">
            Next Stop
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#bdbdbd",
            }}
          />
          <Typography sx={{ fontSize: "10px" }} color="text.secondary">
            Upcoming
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default TrackingRouteProgress;
